import { useEffect, useState } from 'react';

import confettiAnimationData from '@lib/core/src/assets/lottie/success-lottie.json?url';
import Beta from '@lib/core/src/components/Beta';
import { Player } from '@lottiefiles/react-lottie-player';
import { CircularProgress } from '@mui/material';
import { AnimationControls } from 'framer-motion';

import { QuickActionDto, QuickActionDtoType } from '@/api/generated';

import RemainingQuickActions from './RemainingQuickActions';
import SuggestionsButtons from './SuggestionsButtons';
import { ActionsAndButtons, ActionsContainer, Container } from './SuggestionsStyles';
import { useSuggestionsLogic } from './hooks/useSuggestionsLogic';
import EmailQuickAction from './quickActions/EmailQuickAction';
import NoQuickAction from './quickActions/NoQuickAction';
import SummaryQuickAction from './quickActions/SummaryQuickAction';
import TaskFollowUpQuickAction from './quickActions/TaskFollowUpQuickAction';

const quickActionsComponentsMap: Partial<
  Record<
    QuickActionDtoType,
    React.FC<{
      quickAction: QuickActionDto;
      index: number;
      firstQuickActionControls: AnimationControls;
      secondQuickActionControls: AnimationControls;
    }>
  >
> = {
  [QuickActionDtoType.meetingSummarySync]: SummaryQuickAction,
  [QuickActionDtoType.emailReply]: EmailQuickAction,
  [QuickActionDtoType.taskFollowUp]: TaskFollowUpQuickAction
};

const Suggestions = () => {
  const {
    disabled,
    isLoadingQuickActions,
    quickActionsToShow,
    firstQuickActionControls,
    secondQuickActionControls,
    handleDismiss,
    handleSnooze,
    handleApprove
  } = useSuggestionsLogic();
  const [hasShownAtLeastOne, setHasShownAtLeastOne] = useState(false);
  const showQuickActions = !!quickActionsToShow.length;
  useEffect(() => {
    if (showQuickActions && !hasShownAtLeastOne) {
      setHasShownAtLeastOne(true);
    }
  }, [showQuickActions, hasShownAtLeastOne]);

  if (isLoadingQuickActions) {
    return (
      <Container className="suggestions-container">
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container className="suggestions-container">
      {showQuickActions && (
        <Beta sx={{ position: 'absolute', top: '2vh', left: '1vw', zIndex: 1001 }} />
      )}
      <ActionsAndButtons>
        <ActionsContainer>
          {showQuickActions && (
            <RemainingQuickActions firstQuickActionId={quickActionsToShow[0]?.id || ''} />
          )}
          {quickActionsToShow.map((quickAction, index) => {
            const QuickActionComponent = quickActionsComponentsMap[quickAction.type!];
            if (!QuickActionComponent) return null;
            return (
              <QuickActionComponent
                key={quickAction.id}
                quickAction={quickAction}
                index={index}
                firstQuickActionControls={firstQuickActionControls}
                secondQuickActionControls={secondQuickActionControls}
              />
            );
          })}
          {!showQuickActions && <NoQuickAction />}
          {hasShownAtLeastOne && !showQuickActions && (
            <Player
              autoplay
              keepLastFrame
              src={confettiAnimationData}
              style={{ height: '200px', width: '200px' }}
            />
          )}
        </ActionsContainer>
        {showQuickActions && (
          <SuggestionsButtons
            disabled={disabled}
            handleDismiss={handleDismiss}
            handleSnooze={handleSnooze}
            handleApprove={handleApprove}
          />
        )}
      </ActionsAndButtons>
    </Container>
  );
};

export default Suggestions;
